import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableSortLabel,
  Collapse,
  IconButton,
  Box,
  Typography,
  LinearProgress,
} from 'components/mui';
import { AddIcon, RemoveIcon } from 'components/icons';
import { useCustomerList, useCustomerDetail } from 'containers/pages/customers/hooks';
import { getComparator, stableSort } from 'libs/sortList';
import CustomerFormUnified from '../customerFormUnified/CustomerFormUnified';
import CustomerDetailPanel from '../customerDetail/CustomerDetailPanel';
import { Customer, CustomerType } from 'containers/pages/customers/types';

const INITIAL_VISIBLE_COUNT = 50;
const LOAD_MORE_COUNT = 50;
const SCROLL_THRESHOLD = 100;

interface CustomerListProps {
  customers: Customer[];
  customerTypes: CustomerType[];
  onSave: (formData: Record<string, unknown>, isEdit: boolean) => Promise<void>;
}

function CustomerList({ customers, customerTypes, onSave }: CustomerListProps) {
  const { state, handler } = useCustomerList();
  const detail = useCustomerDetail();
  const listRef = useRef<HTMLDivElement>(null);

  const sortedCustomers = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => stableSort(customers, getComparator(state.order, state.orderBy) as any) as Customer[],
    [customers, state.order, state.orderBy],
  );

  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [sortedCustomers, state.order, state.orderBy]);

  useEffect(() => {
    // Find the scrollable .outlet ancestor (SharedLayout's scroll container)
    const el = listRef.current;
    if (!el) return;
    let scrollContainer: HTMLElement | null = el.parentElement;
    while (scrollContainer) {
      const { overflow, overflowY } = getComputedStyle(scrollContainer);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') break;
      scrollContainer = scrollContainer.parentElement;
    }
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer!;
      const nearBottom = scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;

      if (nearBottom && !isLoadingMore && visibleCount < sortedCustomers.length) {
        setIsLoadingMore(true);
        setTimeout(() => {
          setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, sortedCustomers.length));
          setIsLoadingMore(false);
        }, 100);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer!.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, visibleCount, sortedCustomers.length]);

  const visibleRows = useMemo(
    () => sortedCustomers.slice(0, visibleCount),
    [sortedCustomers, visibleCount],
  );

  const totalCols = state.headTableCells.length;

  // Responsive: hide Email on xs, hide Type on xs+sm
  const hideOnMobile = new Set(['WorkEmail']);
  const hideOnTablet = new Set(['TypeTitle']);
  const widerColumns = new Set(['WorkPhone', 'WorkEmail']);

  return (
    <Paper ref={listRef} elevation={1}>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" color="text.secondary">
          Showing {visibleRows.length} of {sortedCustomers.length} customers
        </Typography>
      </Box>

      <TableContainer
        sx={{
          overflowX: 'auto',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              {state.headTableCells.map((cell) => {
                const isActive = state.orderBy === cell.property;
                const direction = isActive ? state.order : 'asc';
                const isMobileHidden = hideOnMobile.has(cell.property);
                const isTabletHidden = hideOnTablet.has(cell.property);

                const minW = widerColumns.has(cell.property) ? 150 : undefined;
                const sxProps = isMobileHidden
                  ? { display: { xs: 'none', sm: 'table-cell' }, minWidth: minW }
                  : isTabletHidden
                    ? { display: { xs: 'none', md: 'table-cell' }, minWidth: minW }
                    : minW ? { minWidth: minW } : undefined;

                return (
                  <TableCell
                    key={cell.id}
                    sortDirection={isActive ? state.order : false}
                    sx={sxProps}
                  >
                    {cell.property ? (
                      <TableSortLabel
                        active={isActive}
                        direction={direction}
                        onClick={handler.onSort(cell.property)}
                      >
                        {cell.label}
                      </TableSortLabel>
                    ) : null}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={totalCols} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No customers found</Typography>
                </TableCell>
              </TableRow>
            )}

            {visibleRows.map((customer) => {
              const isOpen = state.selectedIds.includes(customer.Id);
              return (
                <React.Fragment key={customer.Id}>
                  <TableRow
                    hover
                    onClick={() => detail.openPanel(customer.Id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <IconButton
                        aria-label={isOpen ? 'Collapse' : 'Expand'}
                        aria-expanded={isOpen}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handler.onToggleExpand(customer.Id);
                        }}
                      >
                        {isOpen ? <RemoveIcon /> : <AddIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{customer.Name}</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>{customer.WorkPhone}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, minWidth: 150 }}>
                      {customer.WorkEmail}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {customer.TypeTitle}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ p: 0, overflowX: 'hidden', maxWidth: '100%' }} colSpan={totalCols}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2 }}>
                          <CustomerFormUnified
                            mode="edit"
                            customerId={customer.Id}
                            customerTypes={customerTypes}
                            onSave={onSave}
                          />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}

            {isLoadingMore && (
              <TableRow>
                <TableCell colSpan={totalCols}>
                  <Box sx={{ width: '100%', py: 1 }}>
                    <LinearProgress />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ p: 1, textAlign: 'center' }}>
        {!isLoadingMore && visibleRows.length >= sortedCustomers.length && sortedCustomers.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            End of list
          </Typography>
        )}
      </Box>

      <CustomerDetailPanel
        open={detail.open}
        onClose={detail.closePanel}
        customer={detail.customer}
        loading={detail.loading}
        error={detail.error}
        activeTab={detail.activeTab}
        onTabChange={detail.setActiveTab}
        onRefetch={detail.refetch}
        customerTypes={customerTypes}
        onSave={onSave}
      />
    </Paper>
  );
}

export default CustomerList;
